/**
 * R232q5ad QuestAcceptModal — parchment-scroll modal that pops above the
 * NPC dialogue when a `show_quest_offer` side effect is received.
 *
 * Activation: dispatch `aiwari-quest-offer` window CustomEvent with
 *   detail: { questId, giverNpcId, giverName? }
 *
 * Behaviour:
 *  - Fetches quest metadata via `quest-list-by-giver` RPC (which returns the
 *    full QuestDef list for that giver) and picks the matching id.
 *  - Renders parchment scroll (gradient #E8DCC0 → #D4C29C) with wax-seal
 *    glyph (◈) coloured by quest type, Cinzel headline, EB Garamond italic
 *    body, ◇-marked objective list, reward chips, gold "Принять [Z]" +
 *    dark "Отказаться [Esc]" buttons.
 *  - Animation: scaleY 0→1 unfurl on mount, plus subtle outer glow pulse.
 *  - On accept → sends `quest-accept` to the room and listens for
 *    `quest-accept-result`. On success, dispatches `aiwari-server-npc-close`
 *    (handled inline) + closes self.
 *
 * Heraldic palette: parchment #E8DCC0/#D4C29C, gold #C9A96A, ink #3A2818.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { audio } from '../audio/AudioManager.js';
import { getRoom } from '../game/realmClient.js';

type QuestType =
  | 'kill' | 'kill_named' | 'gather' | 'deliver' | 'talk' | 'escort'
  | 'discover' | 'scout' | 'craft' | 'dungeon' | 'arena_win' | 'faction_rep';

interface QuestRewards {
  xp: number;
  gold_aurei?: number;
  crystals?: number;
  faction_id?: string;
  faction_rep?: number;
  item_choice?: Array<{ item_id: string; name?: string }>;
}

interface QuestObjective {
  kind: QuestType;
  target_id?: string;
  target_name?: string;
  count?: number;
}

interface QuestDef {
  id: string;
  zone_id: string;
  zone_name?: string;
  level_min: number;
  level_max: number;
  type: QuestType;
  title: string;
  description: string;
  objective_text: string;
  completion_text: string;
  giver_npc: string;
  turnin_npc: string;
  objective: QuestObjective;
  rewards: QuestRewards;
}

interface QuestOfferDetail {
  questId: string;
  giverNpcId?: string;
  giverName?: string;
}

interface QuestListResult {
  quests?: QuestDef[];
}

interface AcceptResult {
  ok: boolean;
  reason?: string;
  quest?: QuestDef;
}

const SEAL_COLOR_BY_TYPE: Record<QuestType, string> = {
  kill:        '#A33232', // crimson — combat
  kill_named:  '#A33232',
  gather:      '#5BAE57', // emerald — nature
  deliver:     '#C9A96A', // gold — courier
  talk:        '#C9A96A',
  escort:      '#C9A96A',
  discover:    '#3E6FA5', // azure — exploration
  scout:       '#3E6FA5',
  craft:       '#8E6FB0', // violet — craft
  dungeon:     '#A33232',
  arena_win:   '#A33232',
  faction_rep: '#8E6FB0',
};

const ACCEPT_REASON_RU: Record<string, string> = {
  'no-quest':  'Этот свиток истлел.',
  'level':     'Ты пока недостаточно силён.',
  'prereq':    'Сначала исполни прежнее поручение.',
  'already':   'Этот свиток уже у тебя.',
};

export function QuestAcceptModal() {
  const [questId, setQuestId] = useState<string | null>(null);
  const [giverName, setGiverName] = useState<string>('');
  const [quest, setQuest] = useState<QuestDef | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unfurled, setUnfurled] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Open from custom event.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<QuestOfferDetail>).detail;
      if (!detail?.questId) return;
      setQuestId(detail.questId);
      setGiverName(detail.giverName ?? '');
      setQuest(null);
      setError(null);
      setBusy(true);
      setUnfurled(false);
      const room = getRoom();
      if (!room) { setBusy(false); setError('Связь со Стормвейлом потеряна.'); return; }
      // Ask server for quest details (giver-list returns the full QuestDef).
      if (detail.giverNpcId) {
        room.send('quest-list-by-giver', { giverNpc: detail.giverNpcId });
      } else {
        // No giver hint — clear busy and let the user accept blind, but keep
        // the modal open with stub data.
        setBusy(false);
      }
    };
    window.addEventListener('aiwari-quest-offer', handler as EventListener);
    return () => window.removeEventListener('aiwari-quest-offer', handler as EventListener);
  }, []);

  // Server message subscriptions.
  useEffect(() => {
    const room = getRoom();
    if (!room) return;
    const onList = (msg: QuestListResult) => {
      if (!questId) return;
      const found = (msg.quests ?? []).find((q) => q.id === questId);
      if (found) {
        setQuest(found);
        setError(null);
      } else {
        // The giver might not have it cached client-side (e.g. quest from
        // a hand-crafted dialogue tree). Show a graceful stub.
        setQuest(null);
      }
      setBusy(false);
    };
    const onAccept = (msg: AcceptResult) => {
      setBusy(false);
      if (msg.ok) {
        audio.playNotify('quest-accept');
        // Bridge server accept → local quest state so NpcQuestMarkers hides "!"
        if (questId) {
          window.dispatchEvent(new CustomEvent('aiwari-accept-quest', { detail: { questId } }));
        }
        close();
      } else {
        setError(ACCEPT_REASON_RU[msg.reason ?? ''] ?? `Свиток отвергнут: ${msg.reason ?? 'неизвестно'}`);
      }
    };
    room.onMessage('quest-list-by-giver-result', onList);
    room.onMessage('quest-accept-result', onAccept);
    // Colyseus 0.16: handlers are replaced on remount — no explicit off().
    return () => { /* noop */ };
  }, [questId]);

  // Trigger unfurl animation one frame after mount.
  useEffect(() => {
    if (!questId) return;
    const id = window.requestAnimationFrame(() => setUnfurled(true));
    return () => window.cancelAnimationFrame(id);
  }, [questId]);

  // Keybindings: Z to accept, Esc to decline.
  useEffect(() => {
    if (!questId) return;
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
      } else if (e.key === 'z' || e.key === 'Z' || e.key === 'я' || e.key === 'Я') {
        e.preventDefault();
        accept();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questId, busy]);

  const close = () => {
    setQuestId(null);
    setQuest(null);
    setError(null);
    setBusy(false);
    setUnfurled(false);
  };

  const accept = () => {
    if (!questId || busy) return;
    const room = getRoom();
    if (!room) { setError('Связь со Стормвейлом потеряна.'); return; }
    setBusy(true);
    room.send('quest-accept', { questId });
  };

  const sealColor = useMemo(() => {
    const t = quest?.objective?.kind ?? quest?.type;
    return t ? SEAL_COLOR_BY_TYPE[t] ?? '#C9A96A' : '#C9A96A';
  }, [quest]);

  if (!questId) return null;

  const objectives = quest ? [{
    text: quest.objective_text,
    count: quest.objective.count ?? 1,
    targetName: quest.objective.target_name,
  }] : [];

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center p-6"
      style={{
        background: 'radial-gradient(circle at center, rgba(11,9,7,0.82) 0%, rgba(0,0,0,0.94) 90%)',
        backdropFilter: 'blur(5px)',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      <div
        ref={containerRef}
        className="relative max-w-[640px] w-[92vw] max-h-[86vh] flex flex-col overflow-hidden"
        style={{
          background: 'linear-gradient(180deg, #E8DCC0 0%, #D4C29C 100%)',
          border: '3px solid #5C4520',
          boxShadow: '0 0 60px rgba(201,169,106,0.45), inset 0 0 50px rgba(92,69,32,0.2)',
          fontFamily: 'EB Garamond, Georgia, serif',
          color: '#3A2818',
          transform: unfurled ? 'scaleY(1)' : 'scaleY(0.02)',
          transformOrigin: 'top center',
          transition: 'transform 420ms cubic-bezier(0.22, 1, 0.36, 1)',
        }}
      >
        <Corner pos="tl" />
        <Corner pos="tr" />
        <Corner pos="bl" />
        <Corner pos="br" />

        {/* Wax seal — perched at the top-center of the scroll. */}
        <div className="flex justify-center pt-6">
          <div
            className="flex items-center justify-center text-[26px]"
            style={{
              width: 64,
              height: 64,
              borderRadius: '50%',
              background: `radial-gradient(circle at 32% 28%, ${lighten(sealColor, 0.35)}, ${sealColor} 70%, ${darken(sealColor, 0.4)} 100%)`,
              border: '3px solid #3A2818',
              boxShadow: `0 6px 14px rgba(0,0,0,0.45), inset 0 -4px 8px rgba(0,0,0,0.35), 0 0 22px ${sealColor}55`,
              color: '#0B0907',
              fontFamily: 'Cinzel, serif',
              fontWeight: 800,
              textShadow: '0 1px 0 rgba(255,255,255,0.18)',
            }}
            aria-hidden
          >
            ◈
          </div>
        </div>

        {/* Header */}
        <div className="px-8 pt-3 pb-4 text-center">
          <div
            className="text-[10px] uppercase tracking-[0.32em] mb-1"
            style={{ fontFamily: 'Spectral SC, serif', color: '#8A6B2E' }}
          >
            {giverName ? `Поручение от · ${giverName}` : 'Свиток поручения'}
          </div>
          <h2
            className="text-[26px] leading-tight"
            style={{
              fontFamily: 'Cinzel, serif',
              fontWeight: 700,
              color: '#3A2818',
              letterSpacing: '0.025em',
            }}
          >
            {quest?.title ?? '...'}
          </h2>
          {quest && (
            <div
              className="text-[11px] uppercase tracking-[0.2em] mt-1"
              style={{ fontFamily: 'Spectral SC, serif', color: '#8A6B2E' }}
            >
              {quest.zone_name ?? quest.zone_id} · ур. {quest.level_min}+
            </div>
          )}
        </div>

        <Divider />

        {/* Body */}
        <div className="px-8 py-5 overflow-y-auto flex-1">
          {busy && !quest && (
            <div className="text-center text-[14px] italic" style={{ color: '#8A6B2E' }}>
              Чернила сохнут…
            </div>
          )}

          {error && (
            <div
              className="text-center text-[14px] italic px-4 py-3 mb-4"
              style={{
                background: 'rgba(163, 50, 50, 0.12)',
                color: '#A33232',
                border: '1px solid #A33232',
              }}
            >
              {error}
            </div>
          )}

          {quest ? (
            <>
              <p
                className="text-[16px] leading-relaxed italic mb-5"
                style={{ fontFamily: 'EB Garamond, serif', color: '#3A2818' }}
              >
                «{quest.description || quest.objective_text}»
              </p>

              <div
                className="text-[10px] uppercase tracking-[0.3em] mb-2"
                style={{ fontFamily: 'Spectral SC, serif', color: '#8A6B2E' }}
              >
                Цели
              </div>
              <ul className="mb-5 space-y-1.5">
                {objectives.map((o, idx) => (
                  <li
                    key={idx}
                    className="text-[15px] flex gap-2"
                    style={{ fontFamily: 'EB Garamond, serif', color: '#3A2818' }}
                  >
                    <span style={{ color: '#8A6B2E' }}>◇</span>
                    <span>
                      {o.text}
                      {o.count > 1 && (
                        <span style={{ color: '#5C4520', marginLeft: 6 }}>(0 / {o.count})</span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>

              <div
                className="text-[10px] uppercase tracking-[0.3em] mb-2"
                style={{ fontFamily: 'Spectral SC, serif', color: '#8A6B2E' }}
              >
                Награда
              </div>
              <RewardChips rewards={quest.rewards} />
            </>
          ) : (
            !busy && !error && (
              <p
                className="text-[15px] leading-relaxed italic text-center"
                style={{ fontFamily: 'EB Garamond, serif', color: '#5C4520' }}
              >
                Свиток запечатан. Прими его, чтобы прочесть.
              </p>
            )
          )}
        </div>

        <Divider />

        {/* Footer / actions */}
        <div className="px-8 py-4 flex gap-3 justify-center">
          <button
            type="button"
            onClick={accept}
            disabled={busy}
            className="px-6 py-2 text-[12px] uppercase tracking-[0.18em] hover:scale-[1.03] transition"
            style={{
              background: 'linear-gradient(180deg, #E8C77B 0%, #C9A96A 50%, #8A6B2E 100%)',
              color: '#0B0907',
              border: '2px solid #3A2818',
              fontFamily: 'Cinzel, serif',
              fontWeight: 700,
              cursor: busy ? 'wait' : 'pointer',
              boxShadow: '0 4px 10px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.3)',
              opacity: busy ? 0.6 : 1,
            }}
          >
            Принять <span style={{ opacity: 0.7, marginLeft: 6 }}>[Z]</span>
          </button>
          <button
            type="button"
            onClick={close}
            className="px-6 py-2 text-[12px] uppercase tracking-[0.18em] hover:scale-[1.03] transition"
            style={{
              background: 'linear-gradient(180deg, #2A1F18 0%, #1A130E 100%)',
              color: '#C9A96A',
              border: '2px solid #5C4520',
              fontFamily: 'Cinzel, serif',
              fontWeight: 700,
              cursor: 'pointer',
              boxShadow: '0 4px 10px rgba(0,0,0,0.35)',
            }}
          >
            Отказаться <span style={{ opacity: 0.6, marginLeft: 6 }}>[Esc]</span>
          </button>
        </div>
      </div>
    </div>
  );
}

function RewardChips({ rewards }: { rewards: QuestRewards }) {
  const chips: Array<{ label: string; color: string }> = [];
  if (rewards.xp > 0)                                          chips.push({ label: `${rewards.xp} опыта`, color: '#8A6B2E' });
  if (rewards.gold_aurei && rewards.gold_aurei > 0)            chips.push({ label: `${rewards.gold_aurei} ауреев`, color: '#C9A96A' });
  if (rewards.crystals && rewards.crystals > 0)                chips.push({ label: `${rewards.crystals} кристаллов`, color: '#3E6FA5' });
  if (rewards.faction_rep && rewards.faction_rep > 0)          chips.push({ label: `+${rewards.faction_rep} славы`, color: '#5BAE57' });
  for (const it of rewards.item_choice ?? []) {
    chips.push({ label: it.name ?? it.item_id, color: '#8E6FB0' });
  }
  if (chips.length === 0) {
    return (
      <div className="text-[14px] italic" style={{ color: '#5C4520' }}>
        Слово благодарности и кружка тепла.
      </div>
    );
  }
  return (
    <div className="flex flex-wrap gap-2">
      {chips.map((c, i) => (
        <span
          key={i}
          className="text-[12px] px-3 py-1"
          style={{
            background: 'rgba(232,220,192,0.6)',
            border: `1px solid ${c.color}`,
            color: c.color,
            fontFamily: 'Spectral SC, serif',
            letterSpacing: '0.05em',
          }}
        >
          {c.label}
        </span>
      ))}
    </div>
  );
}

function Divider() {
  return (
    <div
      style={{
        height: 1,
        background: 'linear-gradient(90deg, transparent, #8A6B2E 25%, #8A6B2E 75%, transparent)',
        opacity: 0.6,
      }}
    />
  );
}

function Corner({ pos }: { pos: 'tl' | 'tr' | 'bl' | 'br' }) {
  const cls: Record<typeof pos, string> = {
    tl: 'top-2 left-2',
    tr: 'top-2 right-2 scale-x-[-1]',
    bl: 'bottom-2 left-2 scale-y-[-1]',
    br: 'bottom-2 right-2 scale-x-[-1] scale-y-[-1]',
  };
  return (
    <svg
      className={`absolute ${cls[pos]} w-7 h-7 pointer-events-none`}
      viewBox="0 0 28 28"
      fill="none"
      stroke="#5C4520"
      strokeWidth="1.5"
    >
      <path d="M2 2 L10 2 M2 2 L2 10" />
      <path d="M3 3 Q9 3 9 9" />
      <circle cx="5" cy="5" r="1" fill="#5C4520" />
    </svg>
  );
}

// Tiny colour helpers for the wax-seal radial gradient.
function lighten(hex: string, amt: number): string { return mix(hex, '#FFFFFF', amt); }
function darken(hex: string, amt: number): string  { return mix(hex, '#000000', amt); }
function mix(a: string, b: string, t: number): string {
  const pa = parseHex(a); const pb = parseHex(b);
  const r = Math.round(pa[0] + (pb[0] - pa[0]) * t);
  const g = Math.round(pa[1] + (pb[1] - pa[1]) * t);
  const bl = Math.round(pa[2] + (pb[2] - pa[2]) * t);
  return `rgb(${r}, ${g}, ${bl})`;
}
function parseHex(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  const s = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  return [parseInt(s.slice(0, 2), 16), parseInt(s.slice(2, 4), 16), parseInt(s.slice(4, 6), 16)];
}
